<template>
    <div>
        <Card>
            <!-- 统计概览 -->
            <Row :gutter="16" class="mb-16">
                <Col span="6">
                    <div class="stat-widget primary-widget">
                        <div class="widget-icon">
                            <Icon type="md-globe" />
                        </div>
                        <div class="widget-content">
                            <div class="widget-num">{{ totalRequests }}</div>
                            <div class="widget-label">总请求数</div>
                        </div>
                    </div>
                </Col>
                <Col span="6">
                    <div class="stat-widget error-widget">
                        <div class="widget-icon">
                            <Icon type="md-alert" />
                        </div>
                        <div class="widget-content">
                            <div class="widget-num">{{ errorCount }}</div>
                            <div class="widget-label">本页错误</div>
                        </div>
                    </div>
                </Col>
                <Col span="6">
                    <div class="stat-widget warning-widget">
                        <div class="widget-icon">
                            <Icon type="md-time" />
                        </div>
                        <div class="widget-content">
                            <div class="widget-num">{{ slowRequestCount }}</div>
                            <div class="widget-label">本页慢查询(>2s)</div>
                        </div>
                    </div>
                </Col>
                <Col span="6">
                    <div class="stat-widget success-widget">
                        <div class="widget-icon">
                            <Icon type="md-speedometer" />
                        </div>
                        <div class="widget-content">
                            <div class="widget-num">{{ averageDuration }}ms</div>
                            <div class="widget-label">本页平均响应</div>
                        </div>
                    </div>
                </Col>
            </Row>

            <!-- 快速筛选栏 -->
            <Row :gutter="16">
                <Col span="24">
                    <quick-filter
                        @on-filter="handleQuickFilter"
                        @on-time-range="handleTimeRange"
                        ref="quickFilter" />
                </Col>
            </Row>

            <!-- 高级搜索 -->
            <Row :gutter="16">
                <Col span="24">
                    <search-card
                        :form="mainTable.searchForm"
                        :table-load-event="getRequestLogs"
                        :table-search="mainTable.tableSearch"
                        @on-reset="handleSearchReset" />
                </Col>
            </Row>

            <!-- 工具栏 -->
            <Row :gutter="16">
                <Col span="24">
                    <div class="global-tool-bar">
                        <div class="left-bar">
                            <span class="title">请求日志列表</span>
                            <Tag v-if="filterCount > 0" color="primary" class="filter-count">
                                已筛选 {{ filterCount }} 条
                            </Tag>
                            <Button
                                v-if="hasActiveFilter"
                                size="small"
                                type="text"
                                @click="clearAllFilters"
                                icon="md-close-circle">
                                清除筛选
                            </Button>
                        </div>
                        <div class="right-bar">
                            <Tooltip content="刷新数据" placement="top">
                                <Button
                                    type="default"
                                    size="small"
                                    class="btns"
                                    shape="circle"
                                    icon="md-refresh"
                                    :loading="mainTable.loading"
                                    @click="refreshData" />
                            </Tooltip>
                            <Tooltip content="导出Excel" placement="top">
                                <Button
                                    type="primary"
                                    size="small"
                                    class="btns"
                                    shape="circle"
                                    icon="ios-send"
                                    :loading="exporting"
                                    @click="onExport" />
                            </Tooltip>
                            <Tooltip content="字段说明" placement="top">
                                <Button
                                    type="primary"
                                    size="small"
                                    class="btns"
                                    shape="circle"
                                    icon="md-help-circle"
                                    @click="openCloumnDescModal()" />
                            </Tooltip>
                        </div>
                    </div>
                </Col>
            </Row>



            <!-- 数据表格 -->
            <Row :gutter="16">
                <Col span="24">
                    <stand-pagination-table
                        :columns="mainTable.columns"
                        :data="mainTable.data"
                        :loading="mainTable.loading"
                        :table-search="mainTable.tableSearch"
                        :table-load-event="getRequestLogs"
                        :autoHeight="true"
                        :showPage="true"
                        @on-row-click="handleRowClick"
                        ref="paginationTable" />
                </Col>
            </Row>
        </Card>

        <!-- 详情模态框 -->
        <request-log-detail-modal ref="requestLogDetailModal" />

        <!-- 字段说明模态框 -->
        <request-log-column-desc-modal ref="requestLogColumnDescModal" />

    </div>
</template>

<script>
import search from "@/libs/search";
import StandPaginationTable from '@/components/pagination-table/pagination-table.vue';
import { getRequestLogList } from '@/api/modules/system-management/request-log-management';
import SearchCard from '@/components/searchCard/searchCard.vue';
import { DOWNLOAD_SERVER } from '@/api/modules/files';
import RequestLogDetailModal from './request-log-detail-modal.vue';
import QuickFilter from './quick-filter.vue';
import RequestLogColumnDescModal from './request-log-column-desc-modal.vue';

export default {
    name: "RequestLogManagement",
    components: {
        StandPaginationTable,
        SearchCard,
        RequestLogDetailModal,
        QuickFilter,
        RequestLogColumnDescModal
    },

    data() {
        return {
            exporting: false,
            activeQuickFilter: 'all',
            activeTimeRange: null,

            mainTable: {
                columns: [
                    {
                        title: "时间",
                        key: "timestamp",
                        width: 160,
                        fixed: 'left',
                        align: 'center',
                        render: (h, params) => {
                            const timestamp = params.row.timestamp;
                            if (!timestamp) return h('span', '-');

                            const date = new Date(timestamp);
                            const dateStr = date.toLocaleDateString('zh-CN').replace(/\//g, '-');
                            const timeStr = date.toLocaleTimeString('zh-CN');

                            return h('div', {
                                style: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }
                            }, [
                                h('span', { style: { fontWeight: 'bold' } }, dateStr),
                                h('span', { style: { fontSize: '12px', color: '#999' } }, timeStr)
                            ]);
                        }
                    },
                    {
                        title: "方法",
                        key: "method",
                        width: 80,
                        fixed: 'left',
                        align: 'center',
                        filters: [
                            { label: 'GET', value: 'GET' },
                            { label: 'POST', value: 'POST' },
                            { label: 'PUT', value: 'PUT' },
                            { label: 'DELETE', value: 'DELETE' },
                            { label: 'PATCH', value: 'PATCH' }
                        ],
                        filterMultiple: true,
                        filterMethod(value, row) {
                            return row.Method === value;
                        },
                        render: (h, params) => {
                            const method = params.row.method || '-';
                            const colorMap = {
                                'GET': 'success',
                                'POST': 'primary',
                                'PUT': 'warning',
                                'DELETE': 'error',
                                'PATCH': 'info'
                            };
                            const iconMap = {
                                'GET': 'md-download',
                                'POST': 'md-upload',
                                'PUT': 'md-create',
                                'DELETE': 'md-trash',
                                'PATCH': 'md-build'
                            };

                            return h('Tag', {
                                props: {
                                    color: colorMap[method] || 'default'
                                }
                            }, [
                                h('Icon', {
                                    props: {
                                        type: iconMap[method] || 'md-help',
                                        size: 14
                                    },
                                    style: {
                                        marginRight: '4px'
                                    }
                                }),
                                method
                            ]);
                        }
                    },
                    {
                        title: "路径",
                        key: "path",
                        minWidth: 250,
                        align: 'left',
                        render: (h, params) => {
                            const path = params.row.path;
                            if (!path) return h('span', '-');

                            const isApi = path.startsWith('/api/');
                            return h('span', {
                                style: {
                                    color: isApi ? '#2d8cf0' : '#666',
                                    fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
                                    cursor: 'pointer',
                                    display: 'inline-block',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'middle'
                                },
                                attrs: {
                                    title: path
                                },
                                on: {
                                    click: () => {
                                        this.copyToClipboard(path);
                                        this.$Message.success('路径已复制到剪贴板');
                                    }
                                }
                            }, path);
                        }
                    },
                    {
                        title: "状态",
                        key: "responseStatusCode",
                        width: 90,
                        align: 'center',
                        filters: [
                            { label: '2xx 成功', value: '2xx' },
                            { label: '4xx 客户端错误', value: '4xx' },
                            { label: '5xx 服务器错误', value: '5xx' }
                        ],
                        filterMultiple: true,
                        filterMethod(value, row) {
                            const code = row.responseStatusCode;
                            if (!code) return false;
                            if (value === '2xx') return code >= 200 && code < 300;
                            if (value === '4xx') return code >= 400 && code < 500;
                            if (value === '5xx') return code >= 500;
                            return false;
                        },
                        render: (h, params) => {
                            const code = params.row.responseStatusCode;
                            if (!code) return h('span', '-');

                            let color = 'default';
                            let text = code;

                            if (code >= 200 && code < 300) {
                                color = 'success';
                                text = `${code} 成功`;
                            } else if (code >= 400 && code < 500) {
                                color = 'warning';
                                text = `${code} 错误`;
                            } else if (code >= 500) {
                                color = 'error';
                                text = `${code} 异常`;
                            }

                            return h('Tag', { props: { color } }, text);
                        }
                    },
                    {
                        title: "耗时",
                        key: "duration",
                        width: 100,
                        align: 'center',
                        sortable: true,
                        render: (h, params) => {
                            const duration = params.row.duration;
                            if (!duration && duration !== 0) return h('span', '-');

                            let color = '#666';
                            let icon = null;

                            if (duration > 5000) {
                                color = '#ed4014';
                                icon = 'md-alert';
                            } else if (duration > 2000) {
                                color = '#ff9900';
                                icon = 'md-warning';
                            } else if (duration < 500) {
                                color = '#19be6b';
                                icon = 'md-checkmark-circle';
                            }

                            const children = [h('span', { style: { color } }, `${duration}ms`)];

                            if (icon) {
                                children.unshift(h('Icon', {
                                    props: { type: icon, size: 14 },
                                    style: { marginRight: '4px', color }
                                }));
                            }

                            return h('div', children);
                        }
                    },
                    {
                        title: "用户",
                        key: "userName",
                        width: 120,
                        align: 'center',
                        render: (h, params) => {
                            const userName = params.row.userName;
                            const userId = params.row.userId;

                            if (!userName && !userId) return h('span', '-');

                            return h('div', {
                                style: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }
                            }, [
                                h('span', { style: { fontWeight: 'bold' } }, userName || '-'),
                                userId ? h('span', {
                                    style: { fontSize: '12px', color: '#999', fontFamily: 'monospace' }
                                }, `ID: ${userId}`) : null
                            ]);
                        }
                    },
                    {
                        title: "IP地址",
                        key: "RemoteIpAddress",
                        width: 130,
                        align: 'center',
                        render: (h, params) => {
                            const ip = params.row.remoteIpAddress;
                            if (!ip) return h('span', '-');
                            const isInternal = /^(10\.|172\.(1[6-9]|2[0-9]|3[01])\.|192\.168\.|127\.)/.test(ip);
                            return h('Tag', { props: { color: isInternal ? 'default' : 'primary' } }, ip);
                        }
                    },
                    {
                        title: "大小",
                        key: "sizes",
                        width: 100,
                        align: 'center',
                        render: (h, params) => {
                            const requestSize = params.row.requestSize || 0;
                            const responseSize = params.row.responseSize || 0;
                            const total = requestSize + responseSize;

                            if (total === 0) return h('span', '-');

                            return h('Tooltip', {
                                props: {
                                    content: `请求: ${this.formatFileSize(requestSize)} / 响应: ${this.formatFileSize(responseSize)}`,
                                    placement: 'top'
                                }
                            }, [
                                h('span', {
                                    style: { cursor: 'pointer', color: '#2d8cf0' }
                                }, this.formatFileSize(total))
                            ]);
                        }
                    },
                    {
                        title: "操作",
                        key: "action",
                        width: 100,
                        align: 'center',
                        fixed: 'right',
                        render: (h, params) => {
                            return h('div', [
                                h('Tooltip', { props: { content: '查看详情', placement: 'top' } }, [
                                    h('Button', {
                                        props: { type: 'primary', size: 'small', shape: 'circle', icon: 'md-eye' },
                                        style: { marginRight: '5px' },
                                        on: {
                                            click: (e) => {
                                                e.stopPropagation();
                                                this.showDetail(params.row);
                                            }
                                        }
                                    })
                                ]),
                                h('Tooltip', { props: { content: '复制请求信息', placement: 'top' } }, [
                                    h('Button', {
                                        props: { type: 'default', size: 'small', shape: 'circle', icon: 'md-copy' },
                                        on: {
                                            click: (e) => {
                                                e.stopPropagation();
                                                this.copyRequestInfo(params.row);
                                            }
                                        }
                                    })
                                ])
                            ]);
                        }
                    }
                ],
                data: [],
                searchForm: [
                    {
                        label: '请求路径',
                        type: 'Input',
                        value: '',
                        name: 'path',
                        sql: 'like',
                        placeholder: '支持模糊搜索，如：/api/user'
                    },
                    {
                        label: '用户ID',
                        type: 'Input',
                        value: '',
                        name: 'userid',
                        sql: 'eq',
                        placeholder: '精确匹配用户ID'
                    },
                    {
                        label: '用户名',
                        type: 'Input',
                        value: '',
                        name: 'username',
                        sql: 'like',
                        placeholder: '支持模糊搜索'
                    },
                    {
                        label: 'IP地址',
                        type: 'Input',
                        value: '',
                        name: 'remoteipaddress',
                        sql: 'eq',
                        placeholder: '精确匹配IP地址'
                    },
                    {
                        label: '最小耗时(ms)',
                        type: 'Input',
                        value: '',
                        name: 'duration_gte',
                        sql: 'gte',
                        placeholder: '大于等于该值'
                    },
                    {
                        label: '最大耗时(ms)',
                        type: 'Input',
                        value: '',
                        name: 'duration_lte',
                        sql: 'lte',
                        placeholder: '小于等于该值'
                    },
                    {
                        label: '请求时间',
                        type: 'Daterange',
                        value: '',
                        name: 'timestamp',
                        sql: 'dateBetween',
                        children: [
                            { name: 'timestamp_start', format: 'yyyy-MM-dd HH:mm:ss' },
                            { name: 'timestamp_end', format: 'yyyy-MM-dd HH:mm:ss' }
                        ]
                    }
                ],
                loading: false,
                tableSearch: search.getSearchParams()
            }
        };
    },

    computed: {
        totalRequests() {
            // 使用后端返回的总数
            return this.mainTable.tableSearch.limit.conditions.total || 0;
        },
        errorCount() {
            // 基于当前页面数据显示错误数
            return this.mainTable.data.filter(item => item.ResponseStatusCode >= 400).length;
        },
        slowRequestCount() {
            // 基于当前页面数据显示慢查询数
            return this.mainTable.data.filter(item => item.Duration > 2000).length;
        },
        averageDuration() {
            // 基于当前页面数据计算平均响应时间
            const data = this.mainTable.data;
            if (data.length === 0) return 0;
            const total = data.reduce((sum, item) => sum + (item.Duration || 0), 0);
            return Math.round(total / data.length);
        },
        filterCount() {
            // 显示当前筛选条件下的记录数
            return this.mainTable.tableSearch.limit.conditions.total || 0;
        },
        hasActiveFilter() {
            return this.activeQuickFilter !== 'all' ||
                   this.activeTimeRange !== null ||
                   this.filterCount > 0;
        }
    },

    methods: {
        // 获取请求日志
        getRequestLogs() {
            this.mainTable.loading = true;

            // 同步搜索表单的值到筛选条件
            this.syncSearchFormToFilters();



            getRequestLogList(this.mainTable.tableSearch)
                .then((res) => {
                    this.mainTable.loading = false;
                    if (res.code === 0) {
                        if (res.data && res.data.records) {
                            // 如果返回分页数据
                            this.mainTable.data = res.data.records;
                            search.globalSetFilterRecordCount(this.mainTable.tableSearch, res.data.filterRecordCount || res.data.total || 0);
                        } else if (res.data && Array.isArray(res.data)) {
                            // 如果直接返回数组
                            this.mainTable.data = res.data;
                            search.globalSetFilterRecordCount(this.mainTable.tableSearch, res.data.length);
                        } else {
                            console.warn('返回的数据格式不正确:', res.data);
                            this.mainTable.data = [];
                            this.$Message.warning('返回的数据格式不正确，请检查后端接口');
                        }
                    } else {
                        console.error('API返回错误:', res.message);
                        this.mainTable.data = [];
                        this.$Message.error(res.message || '获取请求日志失败');
                    }
                })
                .catch((error) => {
                    this.mainTable.loading = false;
                    console.error('获取请求日志失败:', error);
                    
                    // 更详细的错误处理
                    let errorMessage = '获取请求日志失败，请检查网络连接';
                    if (error.response) {
                        // 服务器返回了错误状态码
                        console.error('错误状态码:', error.response.status);
                        console.error('错误响应:', error.response.data);
                        const message = error.response.data && error.response.data.message ? error.response.data.message : '未知错误';
                        errorMessage = `服务器错误 (${error.response.status}): ${message}`;
                    } else if (error.request) {
                        // 请求已发出，但没有收到响应
                        console.error('网络请求超时或失败');
                        errorMessage = '网络请求超时或失败，请检查网络连接或联系管理员';
                    } else {
                        // 其他错误
                        console.error('请求配置错误:', error.message);
                        errorMessage = `请求错误: ${error.message}`;
                    }
                    
                    this.mainTable.data = [];
                    this.$Message.error(errorMessage);
                });
        },

        // 处理快速筛选
        handleQuickFilter(type) {
            this.activeQuickFilter = type;

            // 先同步搜索表单的值到筛选条件
            this.syncSearchFormToFilters();

            // 应用新的筛选条件
            switch(type) {
                case 'error':
                    this.addSearchCondition('responsestatuscode', 400, 'gte');
                    break;
                case 'slow':
                    this.addSearchCondition('duration', 2000, 'gte');
                    break;
                case 'post':
                    this.addSearchCondition('method', 'POST');
                    break;
                case 'api':
                    this.addSearchCondition('path', '/api/', 'like');
                    break;
            }

            this.refreshData();
        },

        // 处理时间范围筛选
        handleTimeRange(range) {
            this.activeTimeRange = range;

            const now = new Date();
            let startDate = new Date();

            switch(range) {
                case '1h':
                    startDate.setHours(now.getHours() - 1);
                    break;
                case '24h':
                    startDate.setDate(now.getDate() - 1);
                    break;
                case '7d':
                    startDate.setDate(now.getDate() - 7);
                    break;
                case '30d':
                    startDate.setDate(now.getDate() - 30);
                    break;
            }

            // 更新搜索条件
            this.mainTable.searchForm.forEach(item => {
                if (item.name === 'timestamp') {
                    item.value = [startDate, now];
                }
            });

            // 同步搜索表单的值到筛选条件
            this.syncSearchFormToFilters();

            this.refreshData();
        },

        // 处理搜索重置
        handleSearchReset() {
            this.$refs.quickFilter.reset();
            this.activeQuickFilter = 'all';
            this.activeTimeRange = null;
        },

        // 清除所有筛选
        clearAllFilters() {
            this.handleSearchReset();
            this.clearSearchFilters();
            this.refreshData();
        },

        // 清除搜索筛选条件
        clearSearchFilters() {
            if (this.mainTable.tableSearch.filters && this.mainTable.tableSearch.filters.conditions) {
                this.mainTable.tableSearch.filters.conditions = [];
            }
        },

        // 同步搜索表单的值到筛选条件
        syncSearchFormToFilters() {
            // 确保filters对象存在
            if (!this.mainTable.tableSearch.filters) {
                this.mainTable.tableSearch.filters = { enable: true, conditions: [] };
            }

            // 确保conditions是数组
            if (!Array.isArray(this.mainTable.tableSearch.filters.conditions)) {
                this.mainTable.tableSearch.filters.conditions = [];
            }

            // 保留时间范围的快速筛选条件（如果存在）
            const timeRangeConditions = [];
            if (this.mainTable.tableSearch.filters.conditions) {
                const filtered = this.mainTable.tableSearch.filters.conditions.filter(
                    c => c.Name === 'timestamp_start' || c.Name === 'timestamp_end'
                );
                if (filtered && filtered.length > 0) {
                    timeRangeConditions.push(...filtered);
                }
            }

            // 清空筛选条件
            this.mainTable.tableSearch.filters.conditions = [];

            // 恢复时间范围条件
            if (timeRangeConditions.length > 0) {
                this.mainTable.tableSearch.filters.conditions.push(...timeRangeConditions);
            }

            // 同步搜索表单的值
            this.mainTable.searchForm.forEach(item => {
                if (item.value !== '' && item.value !== null && item.value !== undefined) {
                    // 时间范围特殊处理
                    if (item.type === 'Daterange' && Array.isArray(item.value) && item.value.length === 2) {
                        const startDate = item.value[0];
                        const endDate = item.value[1];

                        if (startDate && endDate) {
                            // 添加开始时间条件
                            this.mainTable.tableSearch.filters.conditions.push({
                                Name: 'timestamp_start',
                                Value: this.formatDate(startDate),
                                Type: 'eq'
                            });

                            // 添加结束时间条件
                            this.mainTable.tableSearch.filters.conditions.push({
                                Name: 'timestamp_end',
                                Value: this.formatDate(endDate),
                                Type: 'eq'
                            });
                        }
                    }
                    // 范围字段特殊处理（duration_gte, duration_lte）
                    else if (item.name.includes('_gte') || item.name.includes('_lte')) {
                        if (item.value) {
                            const baseName = item.name.replace(/_gte|_lte/, '');
                            const type = item.name.includes('_gte') ? 'gte' : 'lte';

                            // 移除已存在的同名条件
                            const existingIndex = this.mainTable.tableSearch.filters.conditions.findIndex(
                                c => c.Name === item.name
                            );
                            if (existingIndex >= 0) {
                                this.mainTable.tableSearch.filters.conditions.splice(existingIndex, 1);
                            }

                            this.mainTable.tableSearch.filters.conditions.push({
                                Name: item.name,
                                Value: item.value,
                                Type: type
                            });
                        }
                    }
                    // 普通字段处理
                    else {
                        // 移除已存在的同名条件
                        const existingIndex = this.mainTable.tableSearch.filters.conditions.findIndex(
                            c => c.Name === item.name
                        );
                        if (existingIndex >= 0) {
                            this.mainTable.tableSearch.filters.conditions.splice(existingIndex, 1);
                        }

                        this.mainTable.tableSearch.filters.conditions.push({
                            Name: item.name,
                            Value: item.value,
                            Type: item.sql || 'eq'
                        });
                    }
                }
            });
        },

        // 格式化日期
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const seconds = String(d.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },

        // 添加搜索条件
        addSearchCondition(name, value, type = 'eq') {
            if (!this.mainTable.tableSearch.filters) {
                this.mainTable.tableSearch.filters = { enable: true, conditions: [] };
            }

            if (!Array.isArray(this.mainTable.tableSearch.filters.conditions)) {
                this.mainTable.tableSearch.filters.conditions = [];
            }

            const existingCondition = this.mainTable.tableSearch.filters.conditions.find(
                c => c.Name === name
            );

            if (existingCondition) {
                existingCondition.Value = value;
            } else {
                this.mainTable.tableSearch.filters.conditions.push({
                    Name: name,
                    Value: value,
                    Type: type
                });
            }
        },

        // 刷新数据
        refreshData() {
            // 重置到第一页
            if (this.mainTable.tableSearch.limit) {
                this.mainTable.tableSearch.limit.conditions.pageStart = 1;
            }
            this.getRequestLogs();
        },

        // 导出数据
        onExport() {
            this.exporting = true;
            const exportParams = JSON.parse(JSON.stringify(this.mainTable.tableSearch));
            exportParams.excelExport = { enable: true };

            getRequestLogList(exportParams)
                .then((res) => {
                    this.exporting = false;
                    if (res.code === 0) {
                        if (res.data && res.data.exportFileName) {
                            let url = DOWNLOAD_SERVER + res.data.exportFileName;
                            this.$downLoadFile(url);
                            this.$Message.success('导出成功');
                        } else {
                            this.$Message.warning('导出功能暂未实现');
                        }
                    } else {
                        this.$Message.error(res.message || '导出失败');
                    }
                })
                .catch((error) => {
                    this.exporting = false;
                    console.error('导出失败:', error);
                    this.$Message.error('导出失败，请稍后重试');
                });
        },

        // 显示详情
        showDetail(row) {
            this.$refs.requestLogDetailModal.open(row);
        },

        // 处理行点击
        handleRowClick(row, index) {
            this.showDetail(row);
        },

        // 复制到剪贴板
        copyToClipboard(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        },

        // 复制请求信息
        copyRequestInfo(row) {
            const info = `请求ID: ${row.RequestId || ''}
时间: ${row.Timestamp ? new Date(row.Timestamp).toLocaleString('zh-CN') : ''}
方法: ${row.Method || ''}
路径: ${row.Path || ''}
状态码: ${row.ResponseStatusCode || ''}
耗时: ${row.Duration || 0}ms
用户ID: ${row.UserId || ''}
用户名: ${row.UserName || ''}
IP地址: ${row.RemoteIpAddress || ''}`;

            this.copyToClipboard(info);
            this.$Message.success('请求信息已复制到剪贴板');
        },

        // 打开字段说明模态框
        openCloumnDescModal() {
            this.$refs.requestLogColumnDescModal.openColumnDescModal();
        },

        // 格式化文件大小
        formatFileSize(bytes) {
            if (!bytes || bytes === 0) return '0 B';
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },


    },

    mounted() {
        // 确保初始化时filters和conditions是正确的对象和数组
        if (!this.mainTable.tableSearch.filters) {
            this.mainTable.tableSearch.filters = { enable: true, conditions: [] };
        }
        if (!Array.isArray(this.mainTable.tableSearch.filters.conditions)) {
            this.mainTable.tableSearch.filters.conditions = [];
        }

        this.getRequestLogs();

        // 设置自动刷新（可选）
        // this.refreshInterval = setInterval(() => {
        //     if (!this.mainTable.loading) {
        //         this.refreshData();
        //     }
        // }, 30000); // 30秒刷新一次
    },

    beforeDestroy() {
        // 清理定时器
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }
};
</script>

<style lang="less" scoped>
.mb-16 {
    margin-bottom: 16px;
}

.stat-widget {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    align-items: center;
    transition: all 0.3s;
    border: 1px solid #eee;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .widget-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        margin-right: 16px;
    }

    .widget-content {
        flex: 1;
        overflow: hidden;

        .widget-num {
            font-size: 24px;
            font-weight: bold;
            line-height: 1.2;
            color: #17233d;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .widget-label {
            font-size: 13px;
            color: #808695;
            margin-top: 4px;
        }
    }

    &.primary-widget {
        .widget-icon {
            background: rgba(45, 140, 240, 0.1);
            color: #2d8cf0;
        }
    }

    &.error-widget {
        .widget-icon {
            background: rgba(237, 64, 20, 0.1);
            color: #ed4014;
        }
        .widget-num { color: #ed4014; }
    }

    &.warning-widget {
        .widget-icon {
            background: rgba(255, 153, 0, 0.1);
            color: #ff9900;
        }
        .widget-num { color: #ff9900; }
    }

    &.success-widget {
        .widget-icon {
            background: rgba(25, 190, 107, 0.1);
            color: #19be6b;
        }
        .widget-num { color: #19be6b; }
    }
}

.global-tool-bar {
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid #f8f8f9;

    .left-bar {
        display: flex;
        align-items: center;
        gap: 8px;

        .title {
            font-size: 16px;
            font-weight: 600;
            color: #17233d;
        }

        .filter-count {
            font-size: 12px;
        }
    }

    .right-bar {
        display: flex;
        gap: 8px;

        .btns {
            margin-left: 8px;
            &:hover {
                color: #2d8cf0;
                background-color: rgba(45, 140, 240, 0.1);
                border-color: rgba(45, 140, 240, 0.2);
            }
        }
    }
}
</style>